import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';

// The localStorage key the auth store persists under (see auth-store.ts).
const AUTH_STORAGE_KEY = 'dfos-auth';

function isAuthedSnapshot(raw: string | null): boolean {
  if (!raw) return false;
  try {
    return JSON.parse(raw)?.state?.isAuthenticated === true;
  } catch {
    return false;
  }
}

export function RootLayout() {
  // Cross-tab logout: the persisted auth store lives in localStorage, but
  // zustand's `persist` does not react to changes made by *other* tabs. When
  // one tab logs out (or its session is cleared), the browser fires a `storage`
  // event in every other tab for the same origin. We listen for that, and if
  // the shared auth state has flipped to signed-out while this tab still thinks
  // it is signed in, we mirror the logout here with a hard redirect so the tab
  // can't keep operating against an invalidated session.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== AUTH_STORAGE_KEY) return;
      const stillAuthedElsewhere = isAuthedSnapshot(e.newValue);
      if (stillAuthedElsewhere) return; // a login / refresh in another tab
      if (!useAuthStore.getState().isAuthenticated) return; // already signed out
      // Mirror the other tab's logout. Clear in-memory state, then hard-redirect
      // so any live view (e.g. the Command Center map) is torn down by the
      // browser rather than React mid-commit.
      useAuthStore.getState().clearSession();
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <div className="min-h-full bg-background text-foreground">
      <Outlet />
    </div>
  );
}
